/**
 * Infinite Scroll Utility
 *
 * Loads the next paginated page via fetch and appends its items to the
 * current grid as the user scrolls toward the bottom.
 *
 * Expected markup (inside any [data-infinite-scroll] container):
 *   <div data-infinite-scroll>
 *     <div data-infinite-grid> ... items ... </div>
 *     <a data-infinite-next href="?page=2" hidden></a>
 *   </div>
 *
 * On each scroll-triggered fetch the new page is parsed, the items inside
 * its [data-infinite-grid] are appended to the existing grid, and the next
 * link's href is updated (or removed if no further pages exist).
 *
 * The snippet/section MutationObservers re-initialize any newly-appended
 * elements that carry data-snippet-script / data-section-script.
 */
import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

export class InfiniteScroll {
  constructor(container) {
    this.container = container;
    this.grid = container.matches('[data-infinite-grid]')
      ? container
      : container.querySelector('[data-infinite-grid]');

    const nextLink = container.querySelector('[data-infinite-next]');
    this.nextUrl = nextLink ? nextLink.getAttribute('href') : null;
    this.loading = false;

    if (!this.grid || !this.nextUrl) return;

    this.container._infiniteScroll = this;
    this.createSentinel();
    this.setupObserver();
  }

  createSentinel() {
    this.sentinel = document.createElement('div');
    this.sentinel.className = 'infinite-scroll-sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');
    this.container.appendChild(this.sentinel);
  }

  setupObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.loading && this.nextUrl) {
            this.loadMore();
          }
        }
      },
      { rootMargin: '400px 0px' }
    );
    this.observer.observe(this.sentinel);
  }

  async loadMore() {
    if (this.loading || !this.nextUrl) return;
    this.loading = true;
    let loaded = false;

    try {
      const response = await fetch(this.nextUrl);
      if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const incomingGrid = doc.querySelector('[data-infinite-grid]');
      const items = incomingGrid ? Array.from(incomingGrid.children) : [];

      // Let listeners tag/hide items before they enter the live grid so a
      // type filter never paints a mismatched product for a frame.
      if (items.length > 0) {
        this.container.dispatchEvent(
          new CustomEvent('infinite-scroll:before-append', {
            bubbles: true,
            detail: { items, container: this.container, grid: this.grid }
          })
        );
        items.forEach((item) => this.grid.appendChild(item));
      }

      const incomingNext = doc.querySelector('[data-infinite-next]');
      if (incomingNext) {
        this.nextUrl = incomingNext.getAttribute('href');
      } else {
        this.nextUrl = null;
        this.observer.disconnect();
      }

      this.container.dispatchEvent(
        new CustomEvent('infinite-scroll:append', {
          bubbles: true,
          detail: { container: this.container, grid: this.grid, items }
        })
      );
      ScrollTrigger.refresh();
      loaded = true;
    } catch (error) {
      console.error('Infinite scroll error:', error);
    } finally {
      this.loading = false;
      if (!this.nextUrl && this.sentinel) {
        this.sentinel.remove();
        this.sentinel = null;
      }
    }

    // Observer only fires on intersection *changes*. After a type filter
    // hides most of a page the sentinel often stays in view, so we have
    // to ask for the next page ourselves.
    if (loaded) this.check();
  }

  /**
   * Load the next page when the sentinel is still near the viewport.
   * Safe to call after a filter pass that collapsed the grid.
   */
  check() {
    if (this.loading || !this.nextUrl || !this.sentinel) return;
    const rect = this.sentinel.getBoundingClientRect();
    if (rect.top <= window.innerHeight + 400) {
      this.loadMore();
    }
  }

  destroy() {
    if (this.observer) this.observer.disconnect();
    if (this.sentinel) this.sentinel.remove();
    if (this.container?._infiniteScroll === this) {
      this.container._infiniteScroll = null;
    }
  }
}

const INIT_ATTR = 'data-infinite-scroll-initialized';

function initOne(el) {
  if (el.hasAttribute(INIT_ATTR)) return;
  new InfiniteScroll(el);
  el.setAttribute(INIT_ATTR, 'true');
}

/**
 * Wire up a freshly-swapped infinite-scroll container — used by the
 * filter swap in collection-products.js after replacing the grid.
 */
export function reinitInfiniteScroll(el) {
  if (!el) return;
  el._infiniteScroll?.destroy();
  el.removeAttribute(INIT_ATTR);
  initOne(el);
}

export function initInfiniteScroll() {
  document.querySelectorAll('[data-infinite-scroll]').forEach(initOne);

  // Watch for newly inserted scroll containers (theme editor / dynamic sections)
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('[data-infinite-scroll]')) initOne(node);
        node.querySelectorAll?.('[data-infinite-scroll]').forEach(initOne);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

export default InfiniteScroll;
